import { useContext, useMemo } from 'react';

import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	createFabricMentionResource,
	XProductUserInviteContext,
} from '@confluence/fabric-media-support/entry-points/MentionsProvider.ts';
import { CoreInvitesContext } from '@confluence/core-invites-provider/entry-points/CoreInvitesContext.ts';

import { useSpaceIdPromise } from './useSpaceIdPromise.ts';

export function useMentionProvider(
	spaceId: string | null | undefined,
	withAnalyticsEventProps?: WithAnalyticsEventsProps,
	focusedMentionId?: string,
) {
	const { cloudId, userId } = useSessionData();
	const { openCoreInvites, userRole } = useContext(CoreInvitesContext);
	const { isEligibleXProductUserInvite, onXProductUserInviteClick } =
		useContext(XProductUserInviteContext); // check if user is eligible for XProductUserInvite

	const spaceIdPromise = useSpaceIdPromise(spaceId);

	return useMemo(
		() =>
			spaceIdPromise.then((spaceId) => {
				return createFabricMentionResource(
					cloudId,
					spaceId,
					userId,
					withAnalyticsEventProps,
					openCoreInvites,
					userRole,
					focusedMentionId,
					isEligibleXProductUserInvite,
					onXProductUserInviteClick,
				);
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			cloudId,
			userId,
			withAnalyticsEventProps,
			openCoreInvites,
			userRole,
			isEligibleXProductUserInvite,
			onXProductUserInviteClick,
		],
	);
}
